/**
 * Fenêtre « Cours » du player V2 : la ou les SKILLCARDS reliées à la question
 * courante, en mode leçon (une carte par onglet) avec son animation publiée
 * (« Voir l'animation », lot 4), puis la règle détaillée et les cours associés.
 * Contenu = DERNIER instantané publié (`get_public_card`), jamais la copie de
 * travail. Consulter la carte ne coûte rien (pas de pénalité HORA).
 * Les outils de la Boîte à outils reliés à la carte (`card_tools`, 0551)
 * s'ouvrent d'un toucher (`onOpenTool`).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ExternalLink, X } from 'lucide-react';
import { LessonMarkdown, MathText, cn } from '@jeraisonne/ui';
import { usePublicCard } from '@/lib/cards/api';
import { ToolChips, linkedTools, type ToolId } from '@/features/toolbox';
import { CardAnimationBlock } from '@/features/animations/card-animation-block';
import { CardLessonView } from './card-lesson-view';
import { CardRuleDetails, cardRuleParts } from './card-rule-details';

export interface LessonCardRef {
  card_id: string;
  title: string;
}

function CardLessonPanel({
  cardId,
  onOpenTool,
}: {
  cardId: string;
  onOpenTool?: (toolId: ToolId) => void;
}) {
  const card = usePublicCard(cardId);
  if (card.isLoading) return <p className="text-ink-500 text-sm">Chargement de la carte…</p>;
  if (card.error || !card.data)
    return <p className="text-ink-500 text-sm">Cette carte n’est pas disponible pour le moment.</p>;
  const { card: snap, lessons } = card.data;
  const tools = onOpenTool ? linkedTools(card.data.tools) : [];
  return (
    <div className="space-y-5">
      {/* animation publiée de la carte (lot 4) : ses outils s'ouvrent PAR-DESSUS la
          fenêtre (le player la fermerait, et l'animation repartirait de zéro) */}
      <CardAnimationBlock cardId={cardId} compact />
      <CardLessonView
        title={snap.title}
        sections={snap.structured_content.sections}
        rule={snap.rule}
      />
      {cardRuleParts(snap).length > 0 && (
        <details className="border-ink-200 group rounded-xl border bg-white">
          <summary className="text-ink-800 flex min-h-[2.75rem] cursor-pointer list-none items-center justify-between gap-2 px-3 text-sm font-semibold">
            Règle détaillée
            <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-3 pb-3">
            <CardRuleDetails card={snap} />
          </div>
        </details>
      )}
      {lessons.length > 0 && (
        <div className="space-y-2">
          <p className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
            Cours associés
          </p>
          {lessons.map((l) => (
            <details key={l.id} className="border-ink-200 group rounded-xl border bg-white">
              <summary className="text-ink-800 flex min-h-[2.75rem] cursor-pointer list-none items-center justify-between gap-2 px-3 text-sm font-semibold">
                <MathText>{l.title}</MathText>
                <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-3 pb-3">
                <LessonMarkdown>{l.content_md}</LessonMarkdown>
              </div>
            </details>
          ))}
        </div>
      )}
      {tools.length > 0 && onOpenTool && (
        <div className="space-y-2">
          <p className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
            {tools.length > 1 ? 'Outils pour manipuler' : 'Outil pour manipuler'}
          </p>
          <ToolChips tools={tools} onOpen={onOpenTool} />
        </div>
      )}
      <Link
        to="/card/$cardId"
        params={{ cardId }}
        target="_blank"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
      >
        Voir la SKILLCARD <ExternalLink className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

export function CardLessonPopup({
  cards,
  onClose,
  onOpenTool,
}: {
  cards: readonly LessonCardRef[];
  onClose: () => void;
  /** ouvre la Boîte à outils sur cet outil (le player ferme le Cours) */
  onOpenTool?: (toolId: ToolId) => void;
}) {
  const [active, setActive] = useState(cards[0]?.card_id ?? '');
  const current = cards.find((c) => c.card_id === active) ?? cards[0];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cours de la question"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="native:pt-[env(safe-area-inset-top)] ocean:bg-[#061433] ocean:shadow-[0_-8px_40px_rgba(0,0,0,0.5)] flex max-h-[92dvh] w-full max-w-2xl flex-col rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-100 flex items-center justify-between gap-2 border-b px-4 py-2">
          <p className="font-display text-ink-900 text-base font-bold">Cours · SKILLCARD</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le cours"
            className="text-ink-500 flex h-11 w-11 items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {cards.length > 1 && (
          <div role="tablist" className="flex gap-2 overflow-x-auto px-4 pt-3">
            {cards.map((c) => (
              <button
                key={c.card_id}
                type="button"
                role="tab"
                aria-selected={c.card_id === current?.card_id}
                onClick={() => setActive(c.card_id)}
                className={cn(
                  'min-h-[2.75rem] shrink-0 rounded-full border px-3 text-sm font-semibold active:scale-[0.98]',
                  c.card_id === current?.card_id
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-ink-200 text-ink-700 bg-white',
                )}
              >
                <MathText>{c.title}</MathText>
              </button>
            ))}
          </div>
        )}
        <div className="overflow-y-auto px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {current ? (
            <CardLessonPanel
              key={current.card_id}
              cardId={current.card_id}
              onOpenTool={onOpenTool}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
